import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Slide, Stack } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';

import { MobileSubtitle } from '../Typography';

export interface MobileCarouselTabItem {
  value: string;
  label: string;
  Component: React.ComponentType;
}

export interface MobileCarouselTabsProps {
  items: MobileCarouselTabItem[];
  value?: string;
  onChange?: (value: string) => void;
}

type SlideDirection = 'left' | 'right';
type NavDirection = 'prev' | 'next';

const MobileCarouselTabs: React.FC<MobileCarouselTabsProps> = ({
  items,
  value,
  onChange,
}) => {
  const isControlled = value !== undefined;
  const [internalIndex, setInternalIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('right');
  const [visible, setVisible] = useState(true);

  const activeIndex = isControlled
    ? Math.max(
        0,
        items.findIndex((item) => item.value === value),
      )
    : internalIndex;

  const contentRef = useRef<HTMLDivElement>(null);

  const navigate = useCallback(
    (dir: NavDirection) => {
      const nextIndex =
        dir === 'next'
          ? (activeIndex + 1) % items.length
          : (activeIndex - 1 + items.length) % items.length;

      // next: new content enters from the right; prev: enters from the left
      const enterDirection: SlideDirection = dir === 'next' ? 'right' : 'left';
      setSlideDirection(enterDirection);
      setVisible(false);

      setTimeout(() => {
        if (isControlled) {
          onChange?.(items[nextIndex].value);
        } else {
          setInternalIndex(nextIndex);
        }
        setVisible(true);
      }, 200);
    },
    [activeIndex, items, isControlled, onChange],
  );

  const handlePrev = useCallback(() => navigate('prev'), [navigate]);
  const handleNext = useCallback(() => navigate('next'), [navigate]);

  const activeItem = items[activeIndex];
  if (activeItem == null) return null;

  const ActiveComponent = activeItem.Component;
  const showChevrons = items.length > 1;

  return (
    <Stack sx={{ flex: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={showChevrons ? 'space-between' : 'center'}
        sx={{ py: 1, px: 0.5 }}
      >
        {showChevrons && (
          <IconButton onClick={handlePrev} size="small" aria-label="Anterior">
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Box
          sx={{
            flex: showChevrons ? 1 : undefined,
            overflow: 'hidden',
            textAlign: 'center',
          }}
        >
          <Slide
            key={activeItem.value}
            in={visible}
            direction={slideDirection}
            mountOnEnter
            unmountOnExit
            timeout={200}
          >
            <Box>
              <MobileSubtitle>{activeItem.label}</MobileSubtitle>
            </Box>
          </Slide>
        </Box>

        {showChevrons && (
          <IconButton onClick={handleNext} size="small" aria-label="Siguiente">
            <ChevronRightIcon />
          </IconButton>
        )}
      </Stack>

      <Box
        ref={contentRef}
        sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}
      >
        <Slide
          key={activeItem.value}
          in={visible}
          direction={slideDirection}
          container={contentRef.current}
          mountOnEnter
          unmountOnExit
          timeout={200}
        >
          <Box sx={{ pb: 2 }}>
            <ActiveComponent />
          </Box>
        </Slide>
      </Box>
    </Stack>
  );
};

export default MobileCarouselTabs;
